import { AttributeRule } from 'API';
import { LocalOrderProductAttributeInput } from 'shared/models';

/**
 * Finds the attribute condition based on the selected order item, attribute type, and selected tooth.
 * @param selectedOrderItem - The selected order item that contains attributes.
 * @param attributeType - The type of attribute to search for.
 * @param selectedTooth - The selected tooth value.
 * @returns The attribute condition that matches the given attribute type and selected tooth, or undefined if not found.
 */
export const findAttributeCondition = <T extends { attributes: LocalOrderProductAttributeInput[] }>(
  selectedOrderItem: T | undefined,
  attributeType: string,
  selectedTooth: string
) => {
  return selectedOrderItem?.attributes.find(attr => attr.type === attributeType && attr.value === selectedTooth);
};

/**
 * Retrieves the attribute names from the given attribute rules.
 * @param attributeRules - An array of attribute rules.
 * @returns An array of attribute names.
 */
export const getAttributeNamesFromAttributeRules = (attributeRules: AttributeRule[]) => {
  return attributeRules.flatMap(rule => rule.displayAttributes.map(attr => attr.name));
};

/**
 * Finds the index of the attribute condition based on the selected order item, attribute type, and selected tooth.
 * @param selectedOrderItem - The selected order item that contains attributes.
 * @param attributeType - The type of attribute to search for.
 * @param selectedTooth - The selected tooth value.
 * @returns The index of the attribute condition that matches the given attribute type and selected tooth, or -1 if not found.
 */
export const findAttributeConditionIndex = <T extends { attributes: LocalOrderProductAttributeInput[] }>(
  selectedOrderItem: T | undefined,
  attributeType: string,
  selectedTooth: string
) => {
  return (
    selectedOrderItem?.attributes.findIndex(attr => attr.type === attributeType && attr.value === selectedTooth) || -1
  );
};
